import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import responseHandler from "responseHandler";
import { MSpinner } from "components/Shared";
import { Button } from "react-md";
import MGridForm from "components/Shared/MGridForm";
import { DATA_TYPE_STRING, DATA_TYPE_DROPDOWN2, DATA_TYPE_MULTI_LINE_STRING } from "typeConstants/dataTypes";
import validator from "components/Shared/MGridFormDefinitions/validator";
import { PermissionToCloseDto } from "interfaces/Mueller.Business.Models";
import { requestPermissionToClose, getPermissionToCloseEmailTemplates } from "requests/closeOut";

interface Props {
  // component props
  controlIdNumber: number;
  sentCallback: () => void;
  // redux props
  makeToast: (text: string, type: string) => void;
}

interface PermissionToCloseForm {
  to: string;
  cc: string;
  body: string;
  bodyTemplate: string;
}

const PH_REFUSED = "PH_REFUSED";
const PH_DECLINED_FINANCIAL = "PH_DECLINED_FINANCIAL";
const PH_NO_RESPONSE = "PH_NO_RESPONSE";

//For the form since we still need the bodyTemplate, but we don't need to send it back
const emptyForm: PermissionToCloseForm = {
  to: "",
  cc: "",
  body: "",
  bodyTemplate: "",
};

const emptyDto: PermissionToCloseDto = {
  to: "",
  cc: "",
  body: "",
};

const UWPermissionToClose = ({
  controlIdNumber,
  sentCallback,
  makeToast,
}: Props) => {

  const mounted = useRef<boolean>(true);
  const formControlMethods = useRef<any>();
  const [formContent, setFormContent] = useState<PermissionToCloseForm>({...emptyForm});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [emailTemplates, setEmailTemplates] = useState<Record<string, string>>(null);
  const [lastUpdate, setLastUpdate] = useState<number>(0);
  const [defaultEmail, setDefaultEmail] = useState<string>(null);
  const [defaultCC, setDefaultCC] = useState<string>(null);

  useEffect(() => {
    getPermissionToCloseEmailTemplates(
      controlIdNumber,
      templates => {
        const next: Record<string, string> = {};
        for (const t of templates.emailBodies) {
          next[t.key] = t.lines.join("\r\n");
        }
        setEmailTemplates(next);
        setDefaultEmail(templates.to);
        setDefaultCC(templates.cc);
        setFormContent({...emptyForm, to: templates.to, cc: templates.cc});
      },
      () => {},
      mounted
    );

    return (() => { mounted.current = false; });
  }, []);

  const getEmailFormDefinition = () => {

    let def = [
      [
        { dataType: DATA_TYPE_STRING, label: "To:", source: "to", hasValidationError: validator.semiColonSeparatedEmailAddressesRequired }
      ],
      [
        { dataType: DATA_TYPE_STRING, label: "CC:", source: "cc", hasValidationError: validator.semiColonSeparatedEmailAddresses }
      ],
      [
        { 
          dataType: DATA_TYPE_DROPDOWN2, 
          label: "Message Template:", 
          source: "bodyTemplate",
          hasValidationError: validator.required,
          dataTypeOptions: { 
            options: [
              { label: "PH Refused Survey", value: PH_REFUSED },
              { label: "PH Declined Financial Data", value: PH_DECLINED_FINANCIAL },
              { label: "PH No Response", value: PH_NO_RESPONSE },
            ] 
          },
          placeholder: "Select a template"
        }
      ],
      [
        { dataType: DATA_TYPE_MULTI_LINE_STRING, label: "Message Body", source: "body", hasValidationError: validator.required }
      ],
    ];

    return def;
  };

  const handleFormChange = (updatedObject: PermissionToCloseForm) => {
    let newFormContent = {...updatedObject};

    if (defaultEmail && !newFormContent.to){
      newFormContent.to = defaultEmail
    }
    else {
      setDefaultEmail(null);
    }

    if (defaultCC && !newFormContent.cc){
      newFormContent.cc = defaultCC
    }
    else {
      setDefaultCC(null);
    }

    //If the template has changed, and we're not selecting the default bogus value, reset the message body.
    if(formContent.bodyTemplate !== updatedObject.bodyTemplate) {
      newFormContent.body = emailTemplates[newFormContent.bodyTemplate];
      setLastUpdate(Date.now());
    }
    
    setFormContent({...newFormContent});
  };

  const onSubmit = () => {
    setIsSubmitting(true);
    let dto: PermissionToCloseDto = {
      to: formContent.to,
      cc: formContent.cc,
      body: formContent.body,
    };

    requestPermissionToClose(controlIdNumber,
      dto,
      () => {
        setIsSubmitting(false);
        makeToast("Email Sent", "success");
        sentCallback && sentCallback();
      },
      () => {
        setIsSubmitting(false);
      },
      mounted
    );
  };

  if (emailTemplates === null) return (<MSpinner />);

  return(
    <div className="uw-permission">
      <div>
        <MGridForm
            // MGridForm doesnt seem to support changing its data out from under it (in this case, updating the email template when we change a dropdown) so we get around this by changing the key whenever we need to, causing the entire component to rerender. This is 'fine' in this case because its a small component and isnt really slowing anything down
            key={`email-form-${lastUpdate}`}
            data={formContent}
            onChange={handleFormChange}
            formDefinition={getEmailFormDefinition()}
            getControlMethods={cm => { formControlMethods.current = cm; }}
            webform
          />
      </div>
      <div>
        <Button
              primary
              raised
              key="send-button"
              onClick={() => { if (formControlMethods.current.isFormValid()) onSubmit(); }}
              disabled={isSubmitting}
              >
              Send
        </Button>
      </div>
    </div>
  ); 
};

const mapDispatchToProps = dispatch => ({
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  }
});

export default connect(null, mapDispatchToProps)(UWPermissionToClose);